import React from 'react';
import {
  Alert,
  AlertIcon,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Controller } from 'react-hook-form';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import MerchantFilterDropdown from 'components/MerchantFilterDropdown';
import {
  YearOptionsForInvoice,
  QuarterOptionsForInvoice,
  selectDaysOptions,
  languageOptions,
  monthsSelectionsOptions,
} from 'utils/StaticData';
import PropTypes from 'prop-types';

const InvoiceGenerationModal = ({
  isOpen,
  onClose,
  handleSubmit,
  register,
  control,
  watch,
  reset,
  clearErrors,
  setValue,
  errors,
  invoiceMerchantFilterHook,
  modalMerchants,
  showWarning,
  handleGenerate,
  isPDFDownload,
  isEmailSending,
  isGenerating,
  isFormIncomplete,
  invoiceJobProgress,
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    isCentered
    closeOnOverlayClick={false}
    size={{ base: 'sm', md: 'md', lg: 'lg' }}
  >
    <ModalOverlay />
    <ModalContent>
      <form onSubmit={handleSubmit(handleGenerate('download'))} noValidate>
        <ModalHeader>Generate Invoices</ModalHeader>
        <ModalCloseButton
          onClick={() => {
            reset();
            clearErrors();
          }}
        />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {showWarning && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  Legal/compliant invoice requires a valid Tax ID (NIF) and full
                  address with postal code.
                </Text>
              </Alert>
            )}
            {(isGenerating || isPDFDownload || isEmailSending) &&
              invoiceJobProgress && (
                <VStack align="stretch" spacing={1}>
                  <Text fontSize="sm">
                    Processing invoices {invoiceJobProgress.done}/
                    {invoiceJobProgress.total}
                  </Text>
                  <Progress
                    value={
                      invoiceJobProgress.total
                        ? (invoiceJobProgress.done / invoiceJobProgress.total) *
                          100
                        : 0
                    }
                    size="sm"
                    borderRadius="md"
                    colorScheme="orange"
                  />
                </VStack>
              )}
            <MerchantFilterDropdown
              searchTerm={invoiceMerchantFilterHook.searchTerm}
              setSearchTerm={invoiceMerchantFilterHook.setSearchTerm}
              selectedMerchantText={invoiceMerchantFilterHook.getSelectedMerchantText()}
              isPopoverOpen={invoiceMerchantFilterHook.isMerchantPopoverOpen}
              openPopover={invoiceMerchantFilterHook.openMerchantPopover}
              closePopover={invoiceMerchantFilterHook.closeMerchantPopover}
              isDisabled={false}
              merchantNameList={modalMerchants}
              filteredMerchants={invoiceMerchantFilterHook.getFilteredMerchants(
                modalMerchants,
              )}
              pendingSelectedMerchant={
                invoiceMerchantFilterHook.pendingSelectedMerchant
              }
              onSelectAll={invoiceMerchantFilterHook.handleSelectAll}
              onMerchantToggle={invoiceMerchantFilterHook.handleMerchantToggle}
              isSelectAllChecked={invoiceMerchantFilterHook.isSelectAllChecked}
              isMerchantChecked={invoiceMerchantFilterHook.isMerchantChecked}
              onApply={invoiceMerchantFilterHook.handleApplySelection}
              onCancel={invoiceMerchantFilterHook.handleCancelSelection}
              onClearSelection={() =>
                invoiceMerchantFilterHook.handleClearSelection(null, () => {})
              }
              merchantGroups={[]}
              selectedGroup={null}
              isGroupsLoading={false}
              onGroupSelection={() => {}}
              onCreateGroup={() => {}}
              onEditGroup={() => {}}
              showGroups={false}
              showSearch={true}
              showSelectAll={true}
              maxHeight="300px"
              buttonWidth="100%"
            />
            <Controller
              name="year"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CommonReactSelect
                  name="year"
                  type="select"
                  options={YearOptionsForInvoice}
                  value={value ? [value] : []}
                  onChange={onChange}
                  register={register}
                  error={errors?.year}
                  getOptionLabel={(e) => e?.label}
                  getOptionValue={(e) => e?.id}
                  placeholderValue="Year"
                />
              )}
            />
            <Controller
              name="daysSelect"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CommonReactSelect
                  name="daysSelect"
                  type="select"
                  options={selectDaysOptions}
                  value={value ? [value] : []}
                  onChange={(selected) => {
                    onChange(selected);
                    if (selected?.id === 2) {
                      setValue('quarter', null);
                      clearErrors('quarter');
                    } else if (selected?.id === 1) {
                      setValue('month', null);
                      clearErrors('month');
                    }
                  }}
                  register={register}
                  error={errors?.daysSelect}
                  getOptionLabel={(e) => e?.label}
                  getOptionValue={(e) => e?.id}
                  placeholderValue="Days"
                />
              )}
            />
            {watch('daysSelect')?.id === 1 ? (
              <Controller
                name="quarter"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CommonReactSelect
                    label="Quarter"
                    name="quarter"
                    type="select"
                    options={QuarterOptionsForInvoice}
                    value={value ? [value] : []}
                    onChange={onChange}
                    register={register}
                    isRequired={true}
                    error={errors?.quarter}
                    getOptionLabel={(e) => e?.label}
                    getOptionValue={(e) => e?.id}
                  />
                )}
              />
            ) : (
              <Controller
                name="month"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CommonReactSelect
                    label="Months"
                    name="month"
                    type="select"
                    options={monthsSelectionsOptions}
                    value={value ? [value] : []}
                    onChange={onChange}
                    register={register}
                    isRequired={true}
                    error={errors?.month}
                    getOptionLabel={(e) => e?.label}
                    getOptionValue={(e) => e?.id}
                  />
                )}
              />
            )}
            <Controller
              name="language"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CommonReactSelect
                  label="Language"
                  name="language"
                  type="select"
                  options={languageOptions}
                  value={value ? [value] : []}
                  onChange={onChange}
                  register={register}
                  error={errors?.language}
                  getOptionLabel={(e) => e?.label}
                  getOptionValue={(e) => e?.id}
                  placeholderValue="Language"
                />
              )}
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            bg="primary"
            color="white"
            _hover={{ bg: 'primary', opacity: 0.8 }}
            mr={3}
            disabled={
              isEmailSending ||
              isPDFDownload ||
              isGenerating ||
              isFormIncomplete
            }
            isLoading={isGenerating}
            loadingText="Generating..."
            fontSize="sm"
            fontWeight="500"
            onClick={handleSubmit(handleGenerate('generate'))}
          >
            Generate
          </Button>
          {/*
            RAT-1712: "Send Mail" is temporarily hidden so invoices aren't
            accidentally emailed to all merchants. No logic removed — restore
            the button by un-commenting this block.
          <Button
            type="button"
            bg="primary"
            color="white"
            _hover={{ bg: 'primary', opacity: 0.8 }}
            mr={3}
            disabled={isEmailSending || isPDFDownload || isFormIncomplete}
            isLoading={isEmailSending}
            loadingText="Sending..."
            fontSize="sm"
            fontWeight="500"
            onClick={handleSubmit(handleGenerate('email'))}
          >
            Send Mail
          </Button>
          */}
          <Button
            type="button"
            bg="primary"
            color="white"
            _hover={{ bg: 'primary', opacity: 0.8 }}
            mr={3}
            disabled={isEmailSending || isPDFDownload}
            isLoading={isPDFDownload}
            loadingText="Generating..."
            fontSize="sm"
            fontWeight="500"
            onClick={handleSubmit(handleGenerate('download'))}
          >
            Download Invoice
          </Button>
        </ModalFooter>
      </form>
    </ModalContent>
  </Modal>
);

InvoiceGenerationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  control: PropTypes.object.isRequired,
  watch: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  invoiceMerchantFilterHook: PropTypes.object.isRequired,
  modalMerchants: PropTypes.array.isRequired,
  showWarning: PropTypes.bool.isRequired,
  handleGenerate: PropTypes.func.isRequired,
  isPDFDownload: PropTypes.bool.isRequired,
  isEmailSending: PropTypes.bool.isRequired,
  isGenerating: PropTypes.bool.isRequired,
  isFormIncomplete: PropTypes.bool.isRequired,
  invoiceJobProgress: PropTypes.shape({
    done: PropTypes.number,
    total: PropTypes.number,
  }),
};

export default InvoiceGenerationModal;
